<template>
  <v-list-item :disabled="disabled">
    <!-- No `mr-3` on the icon: V3's `#prepend` already injects a
         `.v-list-item__spacer` for the gap, so the margin was additive. The gap
         and the item height are set for EVERY action menu (this component and
         the ~98 hand-written `#prepend` items) by the `.v-overlay__content`
         rule in assets/global.css. -->
    <template #prepend>
      <v-icon :color="iconColor">{{ icon }}</v-icon>
    </template>
    <v-list-item-title><slot /></v-list-item-title>
    <!-- An optional keyboard/mouse shortcut for the same action, dim and at
         the item's end, as desktop menus show them. -->
    <template v-if="shortcut" #append>
      <span class="app-menu-item-shortcut">{{ shortcut }}</span>
    </template>
  </v-list-item>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

/*
 * Whole-item wrapper for the app's standard action-menu item
 * (v-list-item with an icon in the `#prepend` slot + v-list-item-title), so the Vuetify
 * 2 -> 3 restructure — v-list-item-icon was REMOVED and the icon moved into v-list-item's
 * `#prepend` slot — is a single-file change instead of touching ~283 hand-written items
 * across ~46 menu files.
 *
 * The caller's @click falls through to the single root <v-list-item> via Vue 3 attribute
 * fall-through (no `emits` declared, inheritAttrs defaults true); v-list-item treats a
 * fell-through onClick as making the item clickable, matching `<v-list-item @click="...">`.
 * iconColor defaults to undefined = no color attr (matches a bare <v-icon>, e.g. the Kit
 * item). `mr-3` preserves the V2 icon-to-title gap; fine-tune against the visual rig.
 */
export default defineComponent({
  name: 'AppMenuItem',
  props: {
    icon: { type: String as PropType<string>, required: true },
    iconColor: {
      type: String as PropType<string | undefined>,
      default: undefined,
    },
    disabled: { type: Boolean as PropType<boolean>, default: false },
    shortcut: {
      type: String as PropType<string | undefined>,
      default: undefined,
    },
  },
})
</script>

<style scoped>
.app-menu-item-shortcut {
  margin-left: 24px;
  font-size: 0.75rem;
  opacity: var(--v-medium-emphasis-opacity);
  white-space: nowrap;
}
</style>
